import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  CircularProgress,
  Alert,
  Button  // <-- CHANGE 1: Imported Button
} from '@mui/material';
import { gql, useQuery, useMutation } from '@apollo/client';
import ChatDetailView from './ChatDetailView';
import PreviousChatsList from './PreviousChatsList';
import EvidencePanel, { Evidence } from './EvidencePanel';
import { useOutletContext } from 'react-router-dom';

// GraphQL Definitions
const LIST_CHAT_SESSIONS_QUERY = gql`
  query ListChatSessions($tenantId: ID!) {
    listChatSessions(tenantId: $tenantId) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

const CREATE_CHAT_SESSION_MUTATION = gql`
  mutation CreateChatSession($input: CreateChatSessionInput!) {
    createChatSession(input: $input) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

const timeRangeOptions: { [key: string]: string } = {
  'last_30_days': 'Last 30 days',
  'last_7_days': 'Last 7 days',
  'last_24_hours': 'Last 24 hours'
};

const CustomSelectIcon = (props: any) => {
    return (
        <img
            src="/icons/arrow-down.svg"
            alt="dropdown icon"
            style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
        />
    );
};

const isToday = (someDate: Date) => {
    const today = new Date();
    return someDate.getDate() === today.getDate() &&
           someDate.getMonth() === today.getMonth() &&
           someDate.getFullYear() === today.getFullYear();
};

const isYesterday = (someDate: Date) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return someDate.getDate() === yesterday.getDate() &&
           someDate.getMonth() === yesterday.getMonth() &&
           someDate.getFullYear() === yesterday.getFullYear();
};

const TroubleshootingPage: React.FC = () => {
  const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
  const [previousChats, setPreviousChats] = useState<ChatSession[]>([]);
  const [evidences, setEvidences] = useState<Evidence[]>([]);
  const [prompt, setPrompt] = useState("");
  const [timeRange, setTimeRange] = useState('last_30_days');
  const [relativeTimeFilter, setRelativeTimeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState("");

  // --- CHANGE 2: Added state for creation error ---
  const [creationError, setCreationError] = useState<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousTenantIdRef = useRef(currentTenant?.id);
  const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
      variables: { tenantId : currentTenant?.id},
      skip: !!activeChatId || !currentTenant,
      fetchPolicy: 'network-only'
  });

  const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

  useEffect(() => {
    if (activeChatId && containerRef.current && chatPanelWidth === null) {
        setChatPanelWidth(containerRef.current.clientWidth * 0.6);
    }
  }, [activeChatId, chatPanelWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
  };

  const handleMouseUp = useCallback(() => {
      setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
      if (isDragging && containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const newChatPanelWidth = e.clientX - containerRect.left;
          const minWidth = containerRect.width * 0.2;
          const maxWidth = containerRect.width * 0.8;
          if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
              setChatPanelWidth(newChatPanelWidth);
          }
      }
  }, [isDragging]);

  useEffect(() => {
      if (isDragging) {
          window.addEventListener('mousemove', handleMouseMove);
          window.addEventListener('mouseup', handleMouseUp);
      } else {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
      }

      return () => {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
      };
  }, [isDragging, handleMouseMove, handleMouseUp]);

 useEffect(() => {
    if (chatsData?.listChatSessions) {
      const chats: ChatSession[] = chatsData.listChatSessions.map((chat: any) => ({
          id: chat.id,
          title: chat.name,
          createdAt: chat.createdAt,
          updatedAt: chat.updatedAt
      }));
      chats.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setPreviousChats(chats);
    }
  }, [chatsData]);

  const filteredChats = useMemo(() => {
    const result = previousChats.filter(chat => {
        const chatDate = new Date(chat.updatedAt);

        if (relativeTimeFilter !== 'all') {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            sevenDaysAgo.setHours(0, 0, 0, 0);

            if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
            if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
            if (relativeTimeFilter === 'last_7_days' && chatDate < sevenDaysAgo) return false;
            if (relativeTimeFilter === 'older' && chatDate >= sevenDaysAgo) return false;
        }

        if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
            return false;
        }
        return true;
    });

    return result.slice(0, 5);

  }, [previousChats, searchTerm, relativeTimeFilter]);

  // --- CHANGE 3: Updated handleStartNewChat to set/clear error state ---
  const handleStartNewChat = async () => {
    setCreationError(null); // Clear previous error on a new attempt

    if (prompt.trim() && !isCreatingChat) {
      try {
        const response = await createChatSession({
            variables: {
                input: {
                    tenantId: currentTenant?.id,
                    initialMessage: prompt,
                }
            }
        });
        const newChat = response.data.createChatSession;
        if (newChat) {
            setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, createdAt: newChat.createdAt, updatedAt: newChat.updatedAt }, ...prev]);
            setInitialPrompt(prompt);
            setEvidences([]);
            setActiveChatId(newChat.id);
            setPrompt("");
        }
      } catch (error: any) {
        console.error("Failed to create chat session:", error);
        setCreationError(error.message || "An unknown error occurred. Please try again.");
      }
    }
  };

  const handleSelectChat = (chatId: string) => {
    setEvidences([]);
    setInitialPrompt(null);
    setActiveChatId(chatId);
  };

  const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
    setEvidences(newEvidences);
  }, []);

  const handleInitialPromptPosted = useCallback(() => {
      setInitialPrompt(null);
  }, []);

  const handleGoBack = useCallback(() => {
    setActiveChatId(null);
    setInitialPrompt(null);
    setChatPanelWidth(null);
    refetchChats();
  }, [refetchChats]);

  useEffect(() => {
    const previousTenantId = previousTenantIdRef.current;
    const currentTenantId = currentTenant?.id;

    if (previousTenantId !== currentTenantId && activeChatId) {
        handleGoBack();
    }
    previousTenantIdRef.current = currentTenantId;
  }, [currentTenant?.id, activeChatId, handleGoBack]);

  const handleLandingKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleStartNewChat();
    }
  };

  const renderLandingPage = () => (
    <Box sx={{
        paddingBottom: 3,
        paddingX: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        height: '100%',
        justifyContent: !chatsLoading && previousChats.length > 0 && !chatsError ? 'flex-start' : 'center',
        paddingTop: !chatsLoading && previousChats.length > 0 && !chatsError ? 10 : 0,
    }}>
      {!chatsError && (
          <>
            <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333', }}>
              What can I help you with today?
            </Typography>
            <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 4, color: '#666666' }}>
              Ask anything about your cloud infrastructure, costs, or performance.
            </Typography>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
              <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
              <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
                {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
              </IconButton>
            </Box>
          </>
      )}

      {/* --- CHANGE 4: Added alert for creation errors --- */}
      {creationError && (
          <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: '656px' }}>
              {creationError}
          </Alert>
      )}

      {(() => {
        if (chatsLoading) return <CircularProgress sx={{ mt: 4 }} />;
        
        // --- CHANGE 5: Replaced Alert with full error display for data fetching ---
        if (chatsError) return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: 3,
              height: '100%',
            }}>
              <img src="/usmerror.svg" alt="Error" style={{ width: '130px', marginBottom: '16px' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                Unable to Display Data
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '300px', mb: 2 }}>
                We're having trouble loading your chats right now. Please try again.
              </Typography>
              <Button
                  variant="text"
                  onClick={() => refetchChats()}
                  sx={{
                      textTransform: 'none',
                      color: '#0064FF',
                      fontWeight: 500,
                      fontFamily:'Inter',
                      lineHeight:'21px',
                      fontSize:'14px',
                  }}
                  >
                  Refresh Page
              </Button>
            </Box>
        );
        
        if (previousChats.length > 0) {
          return (
            <PreviousChatsList
              chats={filteredChats}
              onSelectChat={handleSelectChat}
              onTimeFilterChange={setRelativeTimeFilter}
              timeFilter={relativeTimeFilter}
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
            />
          );
        }
        return null;
      })()}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
        <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
        <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666', mb:2}}>Troubleshoot every issue you face here on the platform</Typography>
      </Box>
      <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
        {activeChatId ? (
          <Box ref={containerRef} sx={{
            display: 'flex',
            height: '100%',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            <Box sx={{
              width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
              minWidth: { md: '200px' },
              height: '100%',
              display: 'flex'
            }}>
              <ChatDetailView
                chatId={activeChatId}
                onBack={handleGoBack}
                userName={userName}
                userProfilePicture={userProfilePicture}
                initialPrompt={initialPrompt}
                onInitialPromptPosted={handleInitialPromptPosted}
                onMessageSelect={handleEvidenceUpdate}
                previousChats={previousChats}
                onNavigateToChat={handleSelectChat}
              />
            </Box>

            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: '12px',
                cursor: 'col-resize',
                backgroundColor: isDragging ? 'transparent' : 'transparent',
                '&:hover': {
                    backgroundColor: 'transparent'
                },
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                userSelect: 'none'
              }}
            >
                <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
            </Box>

            <Box sx={{
              flex: 1,
              minWidth: 0,
              pl: { md: 1 },
              height: '100%',
              display: 'flex'
            }}>
              <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
            </Box>
          </Box>
        ) : (
            <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {previousChats.length > 0 ? 
              <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
              :<img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
              }
              <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
                  {renderLandingPage()}
              </Box>
            </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TroubleshootingPage;



// import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//       updatedAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//       updatedAt
//     }
//   }
// `;

// interface ChatSession {
//   id: string;
//   title: string;
//   createdAt: string;
//   updatedAt: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// // const isThisWeek = (someDate: Date) => {
// //     const today = new Date();
// //     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
// //     firstDayOfWeek.setHours(0, 0, 0, 0);
// //     return someDate.getTime() >= firstDayOfWeek.getTime();
// // };


// const TroubleshootingPage: React.FC = () => {
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatSession[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('all');
//   const [searchTerm, setSearchTerm] = useState("");

//   const [isDragging, setIsDragging] = useState(false);
//   const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
//   const containerRef = useRef<HTMLDivElement>(null);
//    const previousTenantIdRef = useRef(currentTenant?.id);
//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

//   useEffect(() => {
//     if (activeChatId && containerRef.current && chatPanelWidth === null) {
//         setChatPanelWidth(containerRef.current.clientWidth * 0.6);
//     }
//   }, [activeChatId, chatPanelWidth]);

//   const handleMouseDown = (e: React.MouseEvent) => {
//       e.preventDefault();
//       setIsDragging(true);
//   };

//   const handleMouseUp = useCallback(() => {
//       setIsDragging(false);
//   }, []);

//   const handleMouseMove = useCallback((e: MouseEvent) => {
//       if (isDragging && containerRef.current) {
//           const containerRect = containerRef.current.getBoundingClientRect();
//           const newChatPanelWidth = e.clientX - containerRect.left;
//           const minWidth = containerRect.width * 0.2;
//           const maxWidth = containerRect.width * 0.8;
//           if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
//               setChatPanelWidth(newChatPanelWidth);
//           }
//       }
//   }, [isDragging]);

//   useEffect(() => {
//       if (isDragging) {
//           window.addEventListener('mousemove', handleMouseMove);
//           window.addEventListener('mouseup', handleMouseUp);
//       } else {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       }

//       return () => {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       };
//   }, [isDragging, handleMouseMove, handleMouseUp]);

//  useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats: ChatSession[] = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           createdAt: chat.createdAt,
//           updatedAt: chat.updatedAt // <-- ADD THIS LINE
//       }));
//       // --- CHANGE SORTING KEY FROM createdAt TO updatedAt ---
//       chats.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.updatedAt);

//         // --- MODIFIED: Updated filtering logic ---
//         if (relativeTimeFilter !== 'all') {
//             const sevenDaysAgo = new Date();
//             sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
//             sevenDaysAgo.setHours(0, 0, 0, 0);

//             if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//             if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//             if (relativeTimeFilter === 'last_7_days' && chatDate < sevenDaysAgo) return false;
//             if (relativeTimeFilter === 'older' && chatDate >= sevenDaysAgo) return false;
//         }

//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, createdAt: newChat.createdAt, updatedAt: newChat.updatedAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };

//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   // --- CHANGE WAS MADE HERE (wrapped in useCallback) ---
// const handleGoBack = useCallback(() => {
//   setActiveChatId(null);
//   setInitialPrompt(null);
//   setChatPanelWidth(null);
//   refetchChats();
// }, [refetchChats]);
// useEffect(() => {
//   const previousTenantId = previousTenantIdRef.current;
//   const currentTenantId = currentTenant?.id;

//   // If the tenant ID has changed and a chat is active, go back to the landing page
//   if (previousTenantId !== currentTenantId && activeChatId) {
//       handleGoBack();
//   }

//   // Update the ref for the next render
//   previousTenantIdRef.current = currentTenantId;
// }, [currentTenant?.id, activeChatId, handleGoBack]);
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     // --- MODIFIED: This Box now controls the vertical alignment ---
//     <Box sx={{
//         paddingBottom: 3,
//         paddingX: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//         height: '100%',
//         justifyContent: !chatsLoading && previousChats.length > 0 ? 'flex-start' : 'center',
//         paddingTop: !chatsLoading && previousChats.length > 0 ? 10 : 0,
//     }}>
//       {/* This inner box groups the main prompt content */}
      
//           <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333', }}>
//             What can I help you with today?
//           </Typography>
//           <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 4, color: '#666666' }}>
//             Ask anything about your cloud infrastructure, costs, or performance.
//           </Typography>
//           <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//             <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//             <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//               {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//             </IconButton>
//           </Box>
  

//       {/* --- MODIFIED: This entire section is now conditional --- */}
//       {(() => {
//         if (chatsLoading) return <CircularProgress sx={{ mt: 4 }} />;
//         if (chatsError) return <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>;
//         // Only show the list if there are chats to display
//         if (previousChats.length > 0) {
//           return (
//             <PreviousChatsList
//               chats={filteredChats}
//               onSelectChat={handleSelectChat}
//               onTimeFilterChange={setRelativeTimeFilter}
//               timeFilter={relativeTimeFilter}
//               searchTerm={searchTerm}
//               onSearchTermChange={setSearchTerm}
//             />
//           );
//         }
//         // If loading is done, there are no errors, and no chats, render nothing.
//         return null;
//       })()}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666', mb:2}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box ref={containerRef} sx={{
//             display: 'flex',
//             height: '100%',
//             flexDirection: { xs: 'column', md: 'row' }
//           }}>
//             <Box sx={{
//               width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
//               minWidth: { md: '200px' },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <ChatDetailView
//                 chatId={activeChatId}
//                 onBack={handleGoBack}
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//                 previousChats={previousChats}
//                 onNavigateToChat={handleSelectChat}
//               />
//             </Box>

//             <Box
//               onMouseDown={handleMouseDown}
//               sx={{
//                 width: '12px',
//                 cursor: 'col-resize',
//                 backgroundColor: isDragging ? 'transparent' : 'transparent',
//                 '&:hover': {
//                     backgroundColor: 'transparent'
//                 },
//                 display: { xs: 'none', md: 'flex' },
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0,
//                 userSelect: 'none'
//               }}
//             >
//                 <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
//             </Box>

//             <Box sx={{
//               flex: 1,
//               minWidth: 0,
//               pl: { md: 1 },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               {previousChats.length > 0 ? 
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               :<img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               }
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;






// import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// interface ChatSession {
//   id: string;
//   title: string;
//   createdAt: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatSession[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");

//   const [isDragging, setIsDragging] = useState(false);
//   const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
//   const containerRef = useRef<HTMLDivElement>(null);

//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

//   useEffect(() => {
//     if (activeChatId && containerRef.current && chatPanelWidth === null) {
//         setChatPanelWidth(containerRef.current.clientWidth * 0.6);
//     }
//   }, [activeChatId, chatPanelWidth]);

//   const handleMouseDown = (e: React.MouseEvent) => {
//       e.preventDefault();
//       setIsDragging(true);
//   };

//   const handleMouseUp = useCallback(() => {
//       setIsDragging(false);
//   }, []);

//   const handleMouseMove = useCallback((e: MouseEvent) => {
//       if (isDragging && containerRef.current) {
//           const containerRect = containerRef.current.getBoundingClientRect();
//           const newChatPanelWidth = e.clientX - containerRect.left;
//           const minWidth = containerRect.width * 0.2;
//           const maxWidth = containerRect.width * 0.8;
//           if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
//               setChatPanelWidth(newChatPanelWidth);
//           }
//       }
//   }, [isDragging]);

//   useEffect(() => {
//       if (isDragging) {
//           window.addEventListener('mousemove', handleMouseMove);
//           window.addEventListener('mouseup', handleMouseUp);
//       } else {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       }

//       return () => {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       };
//   }, [isDragging, handleMouseMove, handleMouseUp]);

//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats: ChatSession[] = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           createdAt: chat.createdAt
//       }));
//       chats.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.createdAt);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;

//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, createdAt: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };

//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     setChatPanelWidth(null);
//     refetchChats();
//   };
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 10, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333', }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 4, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} onTimeFilterChange={setRelativeTimeFilter} timeFilter={relativeTimeFilter} searchTerm={searchTerm} onSearchTermChange={setSearchTerm}/>}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666', mb:2}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box ref={containerRef} sx={{
//             display: 'flex',
//             height: '100%',
//             flexDirection: { xs: 'column', md: 'row' }
//           }}>
//             <Box sx={{
//               width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
//               minWidth: { md: '200px' },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <ChatDetailView
//                 chatId={activeChatId}
//                 onBack={handleGoBack}
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//                 previousChats={previousChats}
//                 // --- ADDED: Pass the chat selection handler to the detail view ---
//                 onNavigateToChat={handleSelectChat}
//               />
//             </Box>

//             <Box
//               onMouseDown={handleMouseDown}
//               sx={{
//                 width: '12px',
//                 cursor: 'col-resize',
//                 backgroundColor: isDragging ? 'transparent' : 'transparent',
//                 '&:hover': {
//                     backgroundColor: 'transparent'
//                 },
//                 display: { xs: 'none', md: 'flex' },
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0,
//                 userSelect: 'none'
//               }}
//             >
//                 <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
//             </Box>

//             <Box sx={{
//               flex: 1,
//               minWidth: 0,
//               pl: { md: 1 },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // --- FIX 1: Unify the interface to match what ChatDetailView expects ---
// interface ChatSession {
//   id: string;
//   title: string;
//   createdAt: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   // --- FIX 2: Update the state to use the unified ChatSession type ---
//   const [previousChats, setPreviousChats] = useState<ChatSession[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");

//   const [isDragging, setIsDragging] = useState(false);
//   const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
//   const containerRef = useRef<HTMLDivElement>(null);

//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

//   useEffect(() => {
//     if (activeChatId && containerRef.current && chatPanelWidth === null) {
//         setChatPanelWidth(containerRef.current.clientWidth * 0.6);
//     }
//   }, [activeChatId, chatPanelWidth]);

//   const handleMouseDown = (e: React.MouseEvent) => {
//       e.preventDefault();
//       setIsDragging(true);
//   };

//   const handleMouseUp = useCallback(() => {
//       setIsDragging(false);
//   }, []);

//   const handleMouseMove = useCallback((e: MouseEvent) => {
//       if (isDragging && containerRef.current) {
//           const containerRect = containerRef.current.getBoundingClientRect();
//           const newChatPanelWidth = e.clientX - containerRect.left;
//           const minWidth = containerRect.width * 0.2;
//           const maxWidth = containerRect.width * 0.8;
//           if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
//               setChatPanelWidth(newChatPanelWidth);
//           }
//       }
//   }, [isDragging]);

//   useEffect(() => {
//       if (isDragging) {
//           window.addEventListener('mousemove', handleMouseMove);
//           window.addEventListener('mouseup', handleMouseUp);
//       } else {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       }

//       return () => {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       };
//   }, [isDragging, handleMouseMove, handleMouseUp]);

//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       // --- FIX 3: Ensure the mapped object uses `createdAt` instead of `timestamp` ---
//       const chats: ChatSession[] = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           createdAt: chat.createdAt
//       }));
//       chats.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         // --- FIX 4: Filter using the correct `createdAt` property ---
//         const chatDate = new Date(chat.createdAt);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;

//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, createdAt: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };

//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     setChatPanelWidth(null);
//     refetchChats();
//   };
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 10, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333', }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 4, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} onTimeFilterChange={setRelativeTimeFilter} timeFilter={relativeTimeFilter} searchTerm={searchTerm} onSearchTermChange={setSearchTerm}/>}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666', mb:2}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box ref={containerRef} sx={{
//             display: 'flex',
//             height: '100%',
//             flexDirection: { xs: 'column', md: 'row' }
//           }}>
//             <Box sx={{
//               width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
//               minWidth: { md: '200px' },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <ChatDetailView
//                 chatId={activeChatId}
//                 onBack={handleGoBack}
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//                 previousChats={previousChats}
//               />
//             </Box>

//             <Box
//               onMouseDown={handleMouseDown}
//               sx={{
//                 width: '12px',
//                 cursor: 'col-resize',
//                 backgroundColor: isDragging ? 'transparent' : 'transparent',
//                 '&:hover': {
//                     backgroundColor: 'transparent'
//                 },
//                 display: { xs: 'none', md: 'flex' },
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0,
//                 userSelect: 'none'
//               }}
//             >
//                 <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
//             </Box>

//             <Box sx={{
//               flex: 1,
//               minWidth: 0,
//               pl: { md: 1 },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;





// import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");

//   // --- Start of Resizable Panel Logic ---
//   const [isDragging, setIsDragging] = useState(false);
//   const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
//   const containerRef = useRef<HTMLDivElement>(null);
//   // --- End of Resizable Panel Logic ---

//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

//   // --- Start of Resizable Panel Effects ---
//   useEffect(() => {
//     if (activeChatId && containerRef.current && chatPanelWidth === null) {
//         setChatPanelWidth(containerRef.current.clientWidth * 0.6); // Default 60%
//     }
//   }, [activeChatId, chatPanelWidth]);

//   const handleMouseDown = (e: React.MouseEvent) => {
//       e.preventDefault();
//       setIsDragging(true);
//   };

//   const handleMouseUp = useCallback(() => {
//       setIsDragging(false);
//   }, []);

//   const handleMouseMove = useCallback((e: MouseEvent) => {
//       if (isDragging && containerRef.current) {
//           const containerRect = containerRef.current.getBoundingClientRect();
//           const newChatPanelWidth = e.clientX - containerRect.left;
//           // Impose size constraints for the panels
//           const minWidth = containerRect.width * 0.2; // 20%
//           const maxWidth = containerRect.width * 0.8; // 80%
//           if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
//               setChatPanelWidth(newChatPanelWidth);
//           }
//       }
//   }, [isDragging]);

//   useEffect(() => {
//       if (isDragging) {
//           window.addEventListener('mousemove', handleMouseMove);
//           window.addEventListener('mouseup', handleMouseUp);
//       } else {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       }

//       return () => {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       };
//   }, [isDragging, handleMouseMove, handleMouseUp]);
//   // --- End of Resizable Panel Effects ---

//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.timestamp);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;

//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };

//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     setChatPanelWidth(null); // Reset width when going back
//     refetchChats();
//   };
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 10, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333', }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 4, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//       {/* <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}> */}
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} onTimeFilterChange={setRelativeTimeFilter} timeFilter={relativeTimeFilter} searchTerm={searchTerm} onSearchTermChange={setSearchTerm}/>}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666', mb:2}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       {/* Container for search and filters removed from here */}
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box ref={containerRef} sx={{
//             display: 'flex',
//             height: '100%',
//             flexDirection: { xs: 'column', md: 'row' }
//           }}>
//             <Box sx={{
//               width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
//               minWidth: { md: '200px' },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <ChatDetailView
//                 chatId={activeChatId}
//                 onBack={handleGoBack}
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//               />
//             </Box>

//             {/* Draggable Divider */}
//             <Box
//               onMouseDown={handleMouseDown}
//               sx={{
//                 width: '12px',
//                 cursor: 'col-resize',
//                 backgroundColor: isDragging ? 'transparent' : 'transparent',
               
//                 '&:hover': {
//                     backgroundColor: 'transparent'

//                 },
//                 display: { xs: 'none', md: 'flex' },
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0,
//                 userSelect: 'none'
//               }}
//             >
//                 <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
//             </Box>

//             <Box sx={{
//               flex: 1,
//               minWidth: 0,
//               pl: { md: 1 },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");

//   // --- Start of Resizable Panel Logic ---
//   const [isDragging, setIsDragging] = useState(false);
//   const [chatPanelWidth, setChatPanelWidth] = useState<number | null>(null);
//   const containerRef = useRef<HTMLDivElement>(null);
//   // --- End of Resizable Panel Logic ---

//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);

//   // --- Start of Resizable Panel Effects ---
//   useEffect(() => {
//     if (activeChatId && containerRef.current && chatPanelWidth === null) {
//         setChatPanelWidth(containerRef.current.clientWidth * 0.6); // Default 60%
//     }
//   }, [activeChatId, chatPanelWidth]);

//   const handleMouseDown = (e: React.MouseEvent) => {
//       e.preventDefault();
//       setIsDragging(true);
//   };

//   const handleMouseUp = useCallback(() => {
//       setIsDragging(false);
//   }, []);

//   const handleMouseMove = useCallback((e: MouseEvent) => {
//       if (isDragging && containerRef.current) {
//           const containerRect = containerRef.current.getBoundingClientRect();
//           const newChatPanelWidth = e.clientX - containerRect.left;
//           // Impose size constraints for the panels
//           const minWidth = containerRect.width * 0.2; // 20%
//           const maxWidth = containerRect.width * 0.8; // 80%
//           if (newChatPanelWidth > minWidth && newChatPanelWidth < maxWidth) {
//               setChatPanelWidth(newChatPanelWidth);
//           }
//       }
//   }, [isDragging]);

//   useEffect(() => {
//       if (isDragging) {
//           window.addEventListener('mousemove', handleMouseMove);
//           window.addEventListener('mouseup', handleMouseUp);
//       } else {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       }

//       return () => {
//           window.removeEventListener('mousemove', handleMouseMove);
//           window.removeEventListener('mouseup', handleMouseUp);
//       };
//   }, [isDragging, handleMouseMove, handleMouseUp]);
//   // --- End of Resizable Panel Effects ---

//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.timestamp);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;

//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };

//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     setChatPanelWidth(null); // Reset width when going back
//     refetchChats();
//   };
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//       {/* <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}> */}
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} onTimeFilterChange={setRelativeTimeFilter} timeFilter={relativeTimeFilter} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0, mt:1 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                     sx: {
//                       fontFamily:'Inter',
//                       fontWeight: 400,
//                       fontSize: '12px',
//             borderRadius: '8px',
//             bgcolor: '#F9F9F9',
//              minWidth: '200px',
//                       flexGrow: 1,

//             height:'32px',
//             // Keep the default border color
//             '& .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#E6E6E6'
//             },
//             // Ensure the border color doesn't change on hover
//             '&:hover .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#0064FF'
//             },
//             // THIS IS THE FIX: Keep the border color the same when focused
//             '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#0064FF'
//             }
//         }
//                 }}
//              />
//             <FormControl size="small">
//                 {/* <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select> */}
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box ref={containerRef} sx={{
//             display: 'flex',
//             height: '100%',
//             flexDirection: { xs: 'column', md: 'row' }
//           }}>
//             <Box sx={{
//               width: { md: chatPanelWidth ? `${chatPanelWidth}px` : '60%' },
//               minWidth: { md: '200px' },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <ChatDetailView
//                 chatId={activeChatId}
//                 onBack={handleGoBack}
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//               />
//             </Box>

//             {/* Draggable Divider */}
//             <Box
//               onMouseDown={handleMouseDown}
//               sx={{
//                 width: '12px',
//                 cursor: 'col-resize',
//                 backgroundColor: isDragging ? 'transparent' : 'transparent',
               
//                 '&:hover': {
//                     backgroundColor: 'transparent'

//                 },
//                 display: { xs: 'none', md: 'flex' },
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 flexShrink: 0,
//                 userSelect: 'none'
//               }}
//             >
//                 <Box sx={{width: '4px', height: '40px', backgroundColor: '#E0E0E0', borderRadius: '2px'}}/>
//             </Box>

//             <Box sx={{
//               flex: 1,
//               minWidth: 0,
//               pl: { md: 1 },
//               height: '100%',
//               display: 'flex'
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;


// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   // const { userName } = useOutletContext<{ userName?: string | null }>();
//   const { userName, userProfilePicture, currentTenant } = useOutletContext<{ userName?: string | null, userProfilePicture?: string | undefined,currentTenant: { id: string; name: string; } | null }>();
//   //const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId : currentTenant?.id},
//       skip: !!activeChatId || !currentTenant,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);
  
//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.timestamp);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;
        
//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     setEvidences(newEvidences);
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     refetchChats();
//   };
//   const handleLandingKeyPress = (e: React.KeyboardEvent) => {
//     if (e.key === 'Enter' && !e.shiftKey) {
//         e.preventDefault();
//         handleStartNewChat();
//     }
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '100%', maxWidth: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//       {/* <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}> */}
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={handleLandingKeyPress} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:500, fontSize: '14px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} onTimeFilterChange={setRelativeTimeFilter} timeFilter={relativeTimeFilter} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ paddingLeft: '24px', paddingTop: '24px', paddingRight: '24px', flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0, mt:1 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start"><img src="/icons/search_icon.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                     sx: { 
//                       fontFamily:'Inter',
//                       fontWeight: 400,
//                       fontSize: '12px',
//             borderRadius: '8px', 
//             bgcolor: '#F9F9F9', 
//              minWidth: '200px',
//                       flexGrow: 1,
            
//             height:'32px',
//             // Keep the default border color
//             '& .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#E6E6E6'
//             },
//             // Ensure the border color doesn't change on hover
//             '&:hover .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#0064FF'
//             },
//             // THIS IS THE FIX: Keep the border color the same when focused
//             '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                 borderColor: '#0064FF'
//             }
//         }
//                 }}
//              />
//             <FormControl size="small">
//                 {/* <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select> */}
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           // <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//           //   <ChatDetailView 
//           //     chatId={activeChatId} 
//           //     onBack={handleGoBack} 
//           //     userName={userName}
//           //     initialPrompt={initialPrompt}
//           //     onInitialPromptPosted={handleInitialPromptPosted}
//           //     onMessageSelect={handleEvidenceUpdate}
//           //   />
//           //   <EvidencePanel evidences={evidences} />
//           // </Box>
//           <Box sx={{ 
//             display: 'flex', 
//             height: '100%', 
//             flexDirection: { xs: 'column', md: 'row' } 
//           }}>
//             <Box sx={{ 
//               flex: { md: '0 0 40%' }, 
//               minWidth: 0, 
//               pr: { md: 1 }, 
//               mb: { xs: 2, md: 0 },
//               height: '100%', 
//               display: 'flex' 
//             }}>
//               <ChatDetailView 
//                 chatId={activeChatId} 
//                 onBack={handleGoBack} 
//                 userName={userName}
//                 userProfilePicture={userProfilePicture}
//                 initialPrompt={initialPrompt}
//                 onInitialPromptPosted={handleInitialPromptPosted}
//                 onMessageSelect={handleEvidenceUpdate}
//               />
//             </Box>
//             <Box sx={{ 
//               flex: { md: '1 1 60%' }, 
//               minWidth: 0, 
//               pl: { md: 1 },
//               height: '100%', 
//               display: 'flex' 
//             }}>
//               <EvidencePanel evidences={evidences} activeMessage={activeChatId} />
//             </Box>
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;



// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const isToday = (someDate: Date) => {
//     const today = new Date();
//     return someDate.getDate() === today.getDate() &&
//            someDate.getMonth() === today.getMonth() &&
//            someDate.getFullYear() === today.getFullYear();
// };

// const isYesterday = (someDate: Date) => {
//     const yesterday = new Date();
//     yesterday.setDate(yesterday.getDate() - 1);
//     return someDate.getDate() === yesterday.getDate() &&
//            someDate.getMonth() === yesterday.getMonth() &&
//            someDate.getFullYear() === yesterday.getFullYear();
// };

// const isThisWeek = (someDate: Date) => {
//     const today = new Date();
//     const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
//     firstDayOfWeek.setHours(0, 0, 0, 0);
//     return someDate.getTime() >= firstDayOfWeek.getTime();
// };


// const TroubleshootingPage: React.FC = () => {
//   const { userName } = useOutletContext<{ userName?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [relativeTimeFilter, setRelativeTimeFilter] = useState('today');
//   const [searchTerm, setSearchTerm] = useState("");
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);
  
//   const filteredChats = useMemo(() => {
//     const result = previousChats.filter(chat => {
//         const chatDate = new Date(chat.timestamp);

//         if (relativeTimeFilter === 'today' && !isToday(chatDate)) return false;
//         if (relativeTimeFilter === 'yesterday' && !isYesterday(chatDate)) return false;
//         if (relativeTimeFilter === 'this_week' && !isThisWeek(chatDate)) return false;
        
//         if (searchTerm && !chat.title.toLowerCase().includes(searchTerm.toLowerCase())) {
//             return false;
//         }
//         return true;
//     });

//     return result.slice(0, 5);

//   }, [previousChats, searchTerm, relativeTimeFilter]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setEvidences([]);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             const uniqueMap = new Map<string, Evidence>();
//             const combined = [...prev, ...newEvidences];
//             combined.forEach(item => {
//                 uniqueMap.set(item.id, item);
//             });
//             const uniqueEvidences = Array.from(uniqueMap.values());
//             uniqueEvidences.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//             return uniqueEvidences;
//         });
//     }
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//     refetchChats();
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && (
//         <PreviousChatsList
//             chats={filteredChats}
//             onSelectChat={handleSelectChat}
//             timeFilter={relativeTimeFilter}
//             onTimeFilterChange={setRelativeTimeFilter}
//         />
//       )}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Copilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                     sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } }
//                 }}
//              />
//             <FormControl size="small">
//                 {/* <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select> */}
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId}
//               onBack={handleGoBack}
//               userName={userName}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted} onMessageSelect={function (evidences: Evidence[]): void {
//                 throw new Error('Function not implemented.');
//               } }              //onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const TroubleshootingPage: React.FC = () => {
//   const { userName } = useOutletContext<{ userName?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [searchTerm, setSearchTerm] = useState("");
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData, refetch: refetchChats } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);
  
//   const filteredChats = useMemo(() => {
//       if (!searchTerm) {
//           return previousChats;
//       }
//       return previousChats.filter(chat => 
//           chat.title.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//   }, [previousChats, searchTerm]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             const uniqueMap = new Map<string, Evidence>();
//             const combined = [...prev, ...newEvidences];
//             combined.forEach(item => {
//                 uniqueMap.set(item.id, item);
//             });
//             const uniqueEvidences = Array.from(uniqueMap.values());
//             uniqueEvidences.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

//             return uniqueEvidences;
//         });
//     }
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                     sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId} 
//               onBack={handleGoBack} 
//               userName={userName}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted}
//               onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;



// import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const TroubleshootingPage: React.FC = () => {
//   const { userName } = useOutletContext<{ userName?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [searchTerm, setSearchTerm] = useState(""); // State for search functionality
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);
  
//   // Memoized list of chats filtered by the search term
//   const filteredChats = useMemo(() => {
//       if (!searchTerm) {
//           return previousChats;
//       }
//       return previousChats.filter(chat => 
//           chat.title.toLowerCase().includes(searchTerm.toLowerCase())
//       );
//   }, [previousChats, searchTerm]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             const uniqueMap = new Map<string, Evidence>();
//             const combined = [...prev, ...newEvidences];
//             combined.forEach(item => {
//                 uniqueMap.set(item.id, item);
//             });
//             const uniqueEvidences = Array.from(uniqueMap.values());
//             uniqueEvidences.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

//             return uniqueEvidences;
//         });
//     }
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={filteredChats} onSelectChat={handleSelectChat} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>,
//                     sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId} 
//               onBack={handleGoBack} 
//               userName={userName}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted}
//               onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;





// import React, { useState, useEffect, useCallback } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const TroubleshootingPage: React.FC = () => {
//   const { userName } = useOutletContext<{ userName?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       // Sort by timestamp to ensure latest is always first
//       chats.sort((a: { timestamp: string | number | Date; }, b: { timestamp: string | number | Date; }) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
//       setPreviousChats(chats);
//     }
//   }, [chatsData]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   // CORRECTED: The evidence update logic now properly sorts the entire list by timestamp.
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             // Combine old and new evidence
//             const combined = [...prev, ...newEvidences];

//             // Create a map to store unique evidence by ID, ensuring no duplicates
//             const uniqueMap = new Map<string, Evidence>();
//             combined.forEach(item => {
//                 uniqueMap.set(item.id, item);
//             });
//             const uniqueEvidences = Array.from(uniqueMap.values());

//             // Sort the entire unique array by timestamp in descending order (latest first)
//             uniqueEvidences.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

//             return uniqueEvidences;
//         });
//     }
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={previousChats} onSelectChat={handleSelectChat} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField variant="outlined" size="small" placeholder="Search" InputProps={{ startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>, sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } } }}/>
//             <FormControl size="small">
//                 <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId} 
//               onBack={handleGoBack} 
//               userName={userName}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted}
//               onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;





// import React, { useState, useEffect, useCallback } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const TroubleshootingPage: React.FC = () => {
//   const { userName } = useOutletContext<{ userName?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       setPreviousChats(chats.reverse());
//     }
//   }, [chatsData]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt);
//             setActiveChatId(newChat.id);
//             setPrompt("");
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null);
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             const existingIds = new Set(prev.map(e => e.id));
//             const uniqueNewEvidences = newEvidences.filter(e => !existingIds.has(e.id));
//             // Prepend new evidence to show latest first
//             return [...uniqueNewEvidences, ...prev];
//         });
//     }
//   }, []);

//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={previousChats} onSelectChat={handleSelectChat} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField variant="outlined" size="small" placeholder="Search" InputProps={{ startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>, sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } } }}/>
//             <FormControl size="small">
//                 <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId} 
//               onBack={handleGoBack} 
//               userName={userName}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted}
//               onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState, useEffect, useCallback } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
//   Alert
// } from '@mui/material';
// import { gql, useQuery, useMutation } from '@apollo/client';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel';
// import { useOutletContext } from 'react-router-dom';

// // GraphQL Definitions
// const LIST_CHAT_SESSIONS_QUERY = gql`
//   query ListChatSessions($tenantId: ID!) {
//     listChatSessions(tenantId: $tenantId) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// const CREATE_CHAT_SESSION_MUTATION = gql`
//   mutation CreateChatSession($input: CreateChatSessionInput!) {
//     createChatSession(input: $input) {
//       id
//       name
//       createdAt
//     }
//   }
// `;

// // Interfaces
// interface ChatListItem {
//   id: string;
//   title: string;
//   timestamp: string;
// }

// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg"
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };

// const TroubleshootingPage: React.FC = () => {
//   const { userName, userEmail } = useOutletContext<{ userName?: string | null, userEmail?: string | null }>();
//   const tenantId = "6848453777a42773cfd65ac7";

//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [initialPrompt, setInitialPrompt] = useState<string | null>(null); // State for new chat's first message
//   const [previousChats, setPreviousChats] = useState<ChatListItem[]>([]);
//   const [evidences, setEvidences] = useState<Evidence[]>([]);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
  
//   const { loading: chatsLoading, error: chatsError, data: chatsData } = useQuery(LIST_CHAT_SESSIONS_QUERY, {
//       variables: { tenantId },
//       skip: !!activeChatId,
//       fetchPolicy: 'network-only'
//   });

//   const [createChatSession, { loading: isCreatingChat }] = useMutation(CREATE_CHAT_SESSION_MUTATION);
  
//   useEffect(() => {
//     if (chatsData?.listChatSessions) {
//       const chats = chatsData.listChatSessions.map((chat: any) => ({
//           id: chat.id,
//           title: chat.name,
//           timestamp: chat.createdAt
//       }));
//       setPreviousChats(chats.reverse());
//     }
//   }, [chatsData]);

//   const handleStartNewChat = async () => {
//     if (prompt.trim() && !isCreatingChat) {
//       try {
//         const response = await createChatSession({
//             variables: {
//                 input: {
//                     tenantId: tenantId,
//                     initialMessage: prompt,
//                 }
//             }
//         });
//         const newChat = response.data.createChatSession;
//         if (newChat) {
//             setPreviousChats(prev => [{ id: newChat.id, title: newChat.name, timestamp: newChat.createdAt }, ...prev]);
//             setInitialPrompt(prompt); // Set the initial prompt for the new chat
//             setActiveChatId(newChat.id); // Set the active ID
//             setPrompt(""); // Clear the input field
//         }
//       } catch (error) {
//         console.error("Failed to create chat session:", error);
//       }
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     setEvidences([]);
//     setInitialPrompt(null); // Ensure this is null for existing chats
//     setActiveChatId(chatId);
//   };
  
//   const handleEvidenceUpdate = useCallback((newEvidences: Evidence[]) => {
//     if (newEvidences?.length > 0) {
//         setEvidences(prev => {
//             const existingIds = new Set(prev.map(e => e.id));
//             const uniqueNewEvidences = newEvidences.filter(e => !existingIds.has(e.id));
//             return [...prev, ...uniqueNewEvidences];
//         });
//     }
//   }, []);
//   //console.log("evidences-->", evidences);
//   const handleInitialPromptPosted = useCallback(() => {
//       setInitialPrompt(null);
//   }, []);

//   const handleGoBack = () => {
//     setActiveChatId(null);
//     setInitialPrompt(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{ paddingTop: 8, paddingBottom: 3, paddingX: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>
//       <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }} sx={{ width: '656px', height: '130px', borderRadius: '16px', bgcolor: '#FFFFFF', border: '1px solid #E0E0E0', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', position: 'relative', display: 'flex' }}>
//         <TextField fullWidth multiline rows={5} variant="standard" placeholder="Enter your prompt here..." value={prompt} onChange={(e) => setPrompt(e.target.value)} sx={{ p:2, '& .MuiInputBase-root': { height: '100%' } }} InputProps={{ disableUnderline: true, style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' } }} disabled={isCreatingChat} />
//         <IconButton type="submit" sx={{ position: 'absolute', bottom: 16, right: 16, width: 40, height: 40, bgcolor: '#0064FF', color: 'white', '&:hover': { bgcolor: '#0052cc' }, '&.Mui-disabled': { bgcolor: 'grey.300' } }} disabled={!prompt.trim() || isCreatingChat}>
//           {isCreatingChat ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
//       {chatsLoading && <CircularProgress sx={{ mt: 4 }} />}
//       {chatsError && <Alert severity="error" sx={{ mt: 4, width: '656px' }}>Could not load previous chats: {chatsError.message}</Alert>}
//       {!chatsLoading && !chatsError && <PreviousChatsList chats={previousChats} onSelectChat={handleSelectChat} />}
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>Troubleshoot every issue you face here on the platform</Typography>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField variant="outlined" size="small" placeholder="Search" InputProps={{ startAdornment: <InputAdornment position="start"><img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} /></InputAdornment>, sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6' } } }}/>
//             <FormControl size="small">
//                 <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }} IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>{timeRangeOptions[selectedValue as string]}</Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//           <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
//             <ChatDetailView 
//               chatId={activeChatId} 
//               onBack={handleGoBack} 
//               userName={userName}
//               //userEmail={userEmail}
//               initialPrompt={initialPrompt}
//               onInitialPromptPosted={handleInitialPromptPosted}
//               onEvidenceUpdate={handleEvidenceUpdate}
//             />
//             <EvidencePanel evidences={evidences} />
//           </Box>
//         ) : (
//             <Paper elevation={0} sx={{ height: '100%', borderRadius: '12px', bgcolor: 'white', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
//               <img src="/icons/Gradient.svg" alt="background gradient" style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%, -50%)', width: '800px', height: '800px', objectFit: 'contain', opacity: 1, zIndex: 0 }} />
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>
//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';
// import EvidencePanel, { Evidence } from './EvidencePanel'; // Added import for EvidencePanel
// import { useOutletContext } from 'react-router-dom';


// const mockPreviousChats = [
//   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
//   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
//   // MODIFIED: Replaced lorem ipsum with a realistic query
//   { id: 'chat3', title: 'Why did my GCP costs for \'Project: WEB-APP\' spike last night?', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
//   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
//   // MODIFIED: Replaced lorem ipsum with a realistic query
//   { id: 'chat5', title: 'Summarize the unallocated EBS volume costs in \'CUST-X\'.', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// ];
// // const mockPreviousChats = [
// //   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
// //   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
// //   { id: 'chat3', title: 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed...', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
// //   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
// //   { id: 'chat5', title: 'Lorem ipsum dolor sum it...', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// // ];

// const mockConnections = [
//     'Project: CUST-X',
//     'Project: FINOPS-INTERNAL',
//     'Project: WEB-APP',
//     'Project: DATA-PIPELINE',
//     'All Connections'
// ];
// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// // Added mock evidences for the panel
// const mockEvidences: Evidence[] = [
//     {
//         id: 'ev1',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/2 nodes are available: 1 Insufficient cpu, 1 node(s) had untolerated taint {node.kubernetes.io/not-ready: }. preemption: 0/2 nodes are available: 1 No preemption victims found for incoming pod, 1 Preemption is not helpful for scheduling.'
//     },
//     {
//         id: 'ev2',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/2 nodes are available: 1 Insufficient cpu, 1 node(s) had untolerated taint {node.kubernetes.io/not-ready: }. preemption: 0/2 nodes are available: 1 No preemption victims found for incoming pod, 1 Preemption is not helpful for scheduling.'
//     },
//     {
//         id: 'ev3',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/2 nodes are available: 1 Insufficient cpu, 1 node(s) had untolerated taint {node.kubernetes.io/not-ready: }. preemption: 0/2 nodes are available: 1 No preemption victims found for incoming pod, 1 Preemption is not helpful for scheduling.'
//     },
//     {
//         id: 'ev4',
//         project: 'PROJECT: CUST-X',
//         resource: 'pod/data-pod-abc',
//         timestamp: '2024-01-12T20:41:00Z',
//         status: 'Failed to schedule',
//         details: '0/2 nodes are available: 1 Insufficient cpu, 1 node(s) had untolerated taint {node.kubernetes.io/not-ready: }. preemption: 0/2 nodes are available: 1 No preemption victims found for incoming pod, 1 Preemption is not helpful for scheduling.'
//     },
// ];

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg" // Your custom icon path
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };
// const TroubleshootingPage: React.FC = () => {
//   const { userName, userEmail } = useOutletContext<{ userName?: string | null, userEmail?: string | null }>();
//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [connection, setConnection] = useState('All Connections');
//   const [isSending, setIsSending] = useState(false);

//   const handleStartNewChat = () => {
//     if (prompt.trim()) {
//       setIsSending(true);
//       setTimeout(() => {
//         const newChatId = `chat${mockPreviousChats.length + 1}`;
//         setActiveChatId(newChatId);
//         setIsSending(false);
//       }, 1000);
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     const selectedChat = mockPreviousChats.find(c => c.id === chatId);
//     if(selectedChat) {
//         setConnection(selectedChat.connection);
//     }
//     setActiveChatId(chatId);
//   };

//   const handleGoBack = () => {
//     setActiveChatId(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{
//         paddingTop: 8,
//         paddingBottom: 3,
//         paddingX: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//     }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Ask anything about your cloud infrastructure, costs, or performance.
//       </Typography>

//       <Box
//         component="form"
//         onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }}
//         sx={{
//             width: '656px',
//            height: '130px',
//             borderRadius: '16px',
//             bgcolor: '#FFFFFF',
//             border: '1px solid #E0E0E0',
//             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
//             position: 'relative',
            
//             display: 'flex'
//         }}
//       >
//         <TextField
//           fullWidth
//           multiline
//           rows={5}
//           variant="standard"
//           placeholder="Enter your prompt here..."
//           value={prompt}
//           onChange={(e) => setPrompt(e.target.value)}
//           sx={{ 
//            p:2,
//             '& .MuiInputBase-root': {
//               height: '100%'
//             }
//           }}
//           InputProps={{
//             disableUnderline: true,
//             style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' }
//           }}
//           disabled={isSending}
//         />
//         <IconButton
//           type="submit"
//           sx={{
//             position: 'absolute',
//             bottom: 16,
//             right: 16,
//             width: 40,
//             height: 40,
//             bgcolor: '#0064FF',
//             color: 'white',
//             '&:hover': { bgcolor: '#0052cc' },
//             '&.Mui-disabled': { bgcolor: 'grey.300' }
//           }}
//           disabled={!prompt.trim() || isSending}
//         >
//           {isSending ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
      
//       <PreviousChatsList chats={mockPreviousChats} onSelectChat={handleSelectChat} />
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
      
//       {/* Header Section */}
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>CoPilot</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>
//           Troubleshoot every issue you face here on the platform
//         </Typography>
//       </Box>

//       {/* Filter Section */}
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start">
//                             <img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} />
//                         </InputAdornment>,
//                     sx: { 
//                         borderRadius: '8px', 
//                         bgcolor: '#F9F9F9', 
//                         width:'265px', 
//                         height:'32px',
//                         '& .MuiOutlinedInput-notchedOutline': {
//                             borderColor: '#E6E6E6'
//                         }
//                     }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select
//                     value={timeRange}
//                     onChange={(e) => setTimeRange(e.target.value)}
//                     sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }}
//                     IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>
//                           {timeRangeOptions[selectedValue as string]}
//                         </Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>

//       {/* Content Section */}
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         {activeChatId ? (
//     <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
      
//       <ChatDetailView 
//         chatId={activeChatId} 
//         onBack={handleGoBack} 
//         userName={userName} // Pass prop
//         userEmail={userEmail} // Pass prop
//       />
//       <EvidencePanel evidences={mockEvidences} />
//     </Box>
// ) : (
//             // Landing page view
//             <Paper 
//               elevation={0} 
//               sx={{ 
//                 height: '100%',
//                 borderRadius: '12px', 
//                 bgcolor: 'white', 
//                 position: 'relative', 
//                 overflow: 'hidden',
//                 display: 'flex',
//                 flexDirection: 'column'
//               }}
//             >
//               {/* Background Gradient Image */}
//               <img
//                   src="/icons/Gradient.svg" // Your custom image path
//                   alt="background gradient"
//                   style={{
//                     position: 'absolute',
//                     top: '40%',
//                     left: '50%',
//                     transform: 'translate(-50%, -50%)',
//                     width: '800px',
//                     height: '800px',
//                     objectFit: 'contain',
//                     opacity: 1, // Your desired opacity
//                     zIndex: 0,
//                   }}
//               />
//               {/* Scrollable Content */}
//               <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//                   {renderLandingPage()}
//               </Box>
//             </Paper>
//         )}
//       </Box>

//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// // Removed Send as SendIcon as we are using a custom image
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';

// const mockPreviousChats = [
//   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
//   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
//   { id: 'chat3', title: 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed...', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
//   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
//   { id: 'chat5', title: 'Lorem ipsum dolor sum it...', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// ];

// const mockConnections = [
//     'Project: CUST-X',
//     'Project: FINOPS-INTERNAL',
//     'Project: WEB-APP',
//     'Project: DATA-PIPELINE',
//     'All Connections'
// ];
// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg" // Your custom icon path
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };
// const TroubleshootingPage: React.FC = () => {
//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [connection, setConnection] = useState('All Connections');
//   const [isSending, setIsSending] = useState(false);

//   const handleStartNewChat = () => {
//     if (prompt.trim()) {
//       setIsSending(true);
//       setTimeout(() => {
//         const newChatId = `chat${mockPreviousChats.length + 1}`;
//         setActiveChatId(newChatId);
//         setIsSending(false);
//       }, 1000);
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     const selectedChat = mockPreviousChats.find(c => c.id === chatId);
//     if(selectedChat) {
//         setConnection(selectedChat.connection);
//     }
//     setActiveChatId(chatId);
//   };

//   const handleGoBack = () => {
//     setActiveChatId(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{
//         paddingTop: 8,
//         paddingBottom: 3,
//         paddingX: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//     }}>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 500, fontSize:'22px', mb:0.25, color: '#333333' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography sx={{ fontFamily:'Inter', fontWeight: 400, fontSize:'14px', mb: 1, color: '#666666' }}>
//         Lorem ipsum dolor sit amet consectetur adipiscing elit sed
//       </Typography>

//       <Box
//         component="form"
//         onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }}
//         sx={{
//             width: '656px',
//            height: '130px',
//             borderRadius: '16px',
//             bgcolor: '#FFFFFF',
//             border: '1px solid #E0E0E0',
//             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
//             position: 'relative',
            
//             display: 'flex'
//         }}
//       >
//         <TextField
//           fullWidth
//           multiline
//           rows={5}
//           variant="standard"
//           placeholder="Enter your prompt here..."
//           value={prompt}
//           onChange={(e) => setPrompt(e.target.value)}
//           sx={{ 
//            p:2,
//             '& .MuiInputBase-root': {
//               height: '100%'
//             }
//           }}
//           InputProps={{
//             disableUnderline: true,
//             style: { fontFamily:'Inter', fontWeight:400, fontSize: '12px' }
//           }}
//           disabled={isSending}
//         />
//         <IconButton
//           type="submit"
//           sx={{
//             position: 'absolute',
//             bottom: 16,
//             right: 16,
//             width: 40,
//             height: 40,
//             bgcolor: '#0064FF',
//             color: 'white',
//             '&:hover': { bgcolor: '#0052cc' },
//             '&.Mui-disabled': { bgcolor: 'grey.300' }
//           }}
//           disabled={!prompt.trim() || isSending}
//         >
//           {isSending ? <CircularProgress size={24} color="inherit" /> : <img src="/icons/send.svg" alt="send" style={{ width: 40, height: 40, position: 'absolute', pointerEvents: 'none' }}/>}
//         </IconButton>
//       </Box>
      
//       <PreviousChatsList chats={mockPreviousChats} onSelectChat={handleSelectChat} />
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
      
//       {/* Header Section */}
//       <Box sx={{ p: 2, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Cloud Agent</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>
//           Troubleshoot every issues you face here on the platform
//         </Typography>
//       </Box>

//       {/* Filter Section */}
//       <Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start">
//                             <img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} />
//                         </InputAdornment>,
//                     sx: { 
//                         borderRadius: '8px', 
//                         bgcolor: '#F9F9F9', 
//                         width:'265px', 
//                         height:'32px',
//                         '& .MuiOutlinedInput-notchedOutline': {
//                             borderColor: '#E6E6E6'
//                         }
//                     }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select
//                     value={timeRange}
//                     onChange={(e) => setTimeRange(e.target.value)}
//                     sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }}
//                     IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>
//                           {timeRangeOptions[selectedValue as string]}
//                         </Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>

//       {/* Content Section */}
//       <Box sx={{ px: 2, pb: 2, flexGrow: 1, minHeight: 0 }}>
//         <Paper 
//           elevation={0} 
//           sx={{ 
//             height: '100%',
//             borderRadius: '12px', 
//             bgcolor: 'white', 
//             position: 'relative', 
//             overflow: 'hidden',
//             display: 'flex',
//             flexDirection: 'column'
//           }}
//         >
//           {/* Background Gradient Image */}
//           {!activeChatId && (
//             <img
//               src="/icons/Gradient.svg" // Your custom image path
//               alt="background gradient"
//               style={{
//                 position: 'absolute',
//                 top: '40%',
//                 left: '50%',
//                 transform: 'translate(-50%, -50%)',
//                 width: '800px',
//                 //maxWidth: '1200px',
//                 height: '800px',
//                 objectFit: 'contain',
//                 opacity: 1, // Your desired opacity
//                 zIndex: 0,
//               }}
//             />
//           )}

//           {/* Scrollable Content */}
//           <Box sx={{ height: '100%', position: 'relative', zIndex: 1}}>
//             {activeChatId ? (
//               <Box sx={{p:2}}>
//                 <ChatDetailView chatId={activeChatId} onBack={handleGoBack} />
//               </Box>
//             ) : (
//               renderLandingPage()
//             )}
//           </Box>
//         </Paper>
//       </Box>

//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import { Send as SendIcon } from '@mui/icons-material';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';

// const mockPreviousChats = [
//   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
//   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
//   { id: 'chat3', title: 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed...', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
//   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
//   { id: 'chat5', title: 'Lorem ipsum dolor sum it...', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// ];

// const mockConnections = [
//     'Project: CUST-X',
//     'Project: FINOPS-INTERNAL',
//     'Project: WEB-APP',
//     'Project: DATA-PIPELINE',
//     'All Connections'
// ];
// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg" // Your custom icon path
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };
// const TroubleshootingPage: React.FC = () => {
//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [connection, setConnection] = useState('All Connections');
//   const [isSending, setIsSending] = useState(false);

//   const handleStartNewChat = () => {
//     if (prompt.trim()) {
//       setIsSending(true);
//       setTimeout(() => {
//         const newChatId = `chat${mockPreviousChats.length + 1}`;
//         setActiveChatId(newChatId);
//         setIsSending(false);
//       }, 1000);
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     const selectedChat = mockPreviousChats.find(c => c.id === chatId);
//     if(selectedChat) {
//         setConnection(selectedChat.connection);
//     }
//     setActiveChatId(chatId);
//   };

//   const handleGoBack = () => {
//     setActiveChatId(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{
//         p: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//     }}>
//       <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: '#2C3E50' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography color="text.secondary" sx={{ mb: 4 }}>
//         Lorem ipsum dolor sit amet consectetur adipiscing elit sed
//       </Typography>

//       <Box
//         component="form"
//         onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }}
//         sx={{
//             p: 1.5,
//             display: 'flex',
//             alignItems: 'center',
//             width: '100%',
//             maxWidth: 700,
//             borderRadius: '28px',
//             bgcolor: 'rgba(255, 255, 255, 0.8)',
//             backdropFilter: 'blur(10px)',
//             border: '1px solid rgba(0, 0, 0, 0.1)',
//             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
//             position: 'relative'
//         }}
//       >
//         <TextField
//           fullWidth
//           variant="standard"
//           placeholder="Enter your prompt here..."
//           value={prompt}
//           onChange={(e) => setPrompt(e.target.value)}
//           sx={{ ml: 2, flex: 1 }}
//           InputProps={{
//             disableUnderline: true,
//             style: { fontSize: '1rem' }
//           }}
//           disabled={isSending}
//         />
//         <IconButton
//           type="submit"
//           sx={{
//             width: 40,
//             height: 40,
//             bgcolor: '#0064FF',
//             color: 'white',
//             '&:hover': { bgcolor: '#0052cc' },
//             '&.Mui-disabled': { bgcolor: 'grey.300' }
//           }}
//           disabled={!prompt.trim() || isSending}
//         >
//           {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon sx={{ transform: 'rotate(-45deg)', mr: -0.5, mt: 0.5 }} />}
//         </IconButton>
//       </Box>
      
//       <PreviousChatsList chats={mockPreviousChats} onSelectChat={handleSelectChat} />
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', height: '100%', bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
      
//       {/* Header Section */}
//       <Box sx={{ p: 3, flexShrink: 0 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Cloud Agent</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>
//           Troubleshoot every issues you face here on the platform
//         </Typography>
//       </Box>

//       {/* Filter Section */}
//       <Box sx={{ px: 3, pb: 3, flexShrink: 0 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start">
//                             <img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} />
//                         </InputAdornment>,
//                     sx: { 
//                         borderRadius: '8px', 
//                         bgcolor: '#F9F9F9', 
//                         width:'265px', 
//                         height:'32px',
//                         '& .MuiOutlinedInput-notchedOutline': {
//                             borderColor: '#E6E6E6'
//                         }
//                     }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select
//                     value={timeRange}
//                     onChange={(e) => setTimeRange(e.target.value)}
//                     sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }}
//                     IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>
//                           {timeRangeOptions[selectedValue as string]}
//                         </Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>

//       {/* Content Section */}
//       <Box sx={{ px: 3, pb: 3, flexGrow: 1, minHeight: 0 }}>
//         <Paper 
//           elevation={0} 
//           sx={{ 
//             height: '100%',
//             borderRadius: '12px', 
//             bgcolor: 'white', 
//             position: 'relative', 
//             overflow: 'hidden',
//             display: 'flex',
//             flexDirection: 'column'
//           }}
//         >
//           {/* Background Gradient Image */}
//           {!activeChatId && (
//             <img
//               src="/icons/Gradient.svg" // Your custom image path
//               alt="background gradient"
//               style={{
//                 position: 'absolute',
//                 top: '50%',
//                 left: '50%',
//                 transform: 'translate(-50%, -50%)',
//                 width: '100%',
//                 maxWidth: '1200px',
//                 height: 'auto',
//                 objectFit: 'contain',
//                 opacity: 1, // Your desired opacity
//                 zIndex: 0,
//               }}
//             />
//           )}

//           {/* Scrollable Content */}
//           <Box sx={{ height: '100%', overflowY: 'auto', position: 'relative', zIndex: 1}}>
//             {activeChatId ? (
//               <Box sx={{p:3}}>
//                 <ChatDetailView chatId={activeChatId} onBack={handleGoBack} />
//               </Box>
//             ) : (
//               renderLandingPage()
//             )}
//           </Box>
//         </Paper>
//       </Box>

//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import { Send as SendIcon } from '@mui/icons-material';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';

// const mockPreviousChats = [
//   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
//   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
//   { id: 'chat3', title: 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed...', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
//   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
//   { id: 'chat5', title: 'Lorem ipsum dolor sum it...', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// ];

// const mockConnections = [
//     'Project: CUST-X',
//     'Project: FINOPS-INTERNAL',
//     'Project: WEB-APP',
//     'Project: DATA-PIPELINE',
//     'All Connections'
// ];
// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg" // Your custom icon path
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };
// const TroubleshootingPage: React.FC = () => {
//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [prompt, setPrompt] = useState("");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [connection, setConnection] = useState('All Connections');
//   const [isSending, setIsSending] = useState(false);

//   const handleStartNewChat = () => {
//     if (prompt.trim()) {
//       setIsSending(true);
//       setTimeout(() => {
//         const newChatId = `chat${mockPreviousChats.length + 1}`;
//         setActiveChatId(newChatId);
//         setIsSending(false);
//       }, 1000);
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     const selectedChat = mockPreviousChats.find(c => c.id === chatId);
//     if(selectedChat) {
//         setConnection(selectedChat.connection);
//     }
//     setActiveChatId(chatId);
//   };

//   const handleGoBack = () => {
//     setActiveChatId(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{
//         p: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//     }}>
//       <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: '#2C3E50' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography color="text.secondary" sx={{ mb: 4 }}>
//         Lorem ipsum dolor sit amet consectetur adipiscing elit sed
//       </Typography>

//       <Box
//         component="form"
//         onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }}
//         sx={{
//             p: 1.5,
//             display: 'flex',
//             alignItems: 'center',
//             width: '100%',
//             maxWidth: 700,
//             borderRadius: '28px',
//             bgcolor: 'rgba(255, 255, 255, 0.8)',
//             backdropFilter: 'blur(10px)',
//             border: '1px solid rgba(0, 0, 0, 0.1)',
//             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
//             position: 'relative'
//         }}
//       >
//         <TextField
//           fullWidth
//           variant="standard"
//           placeholder="Enter your prompt here..."
//           value={prompt}
//           onChange={(e) => setPrompt(e.target.value)}
//           sx={{ ml: 2, flex: 1 }}
//           InputProps={{
//             disableUnderline: true,
//             style: { fontSize: '1rem' }
//           }}
//           disabled={isSending}
//         />
//         <IconButton
//           type="submit"
//           sx={{
//             width: 40,
//             height: 40,
//             bgcolor: '#0064FF',
//             color: 'white',
//             '&:hover': { bgcolor: '#0052cc' },
//             '&.Mui-disabled': { bgcolor: 'grey.300' }
//           }}
//           disabled={!prompt.trim() || isSending}
//         >
//           {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon sx={{ transform: 'rotate(-45deg)', mr: -0.5, mt: 0.5 }} />}
//         </IconButton>
//       </Box>
      
//       <PreviousChatsList chats={mockPreviousChats} onSelectChat={handleSelectChat} />
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', bgcolor: '#F9FAFB', minHeight: '100%' }}>
      
//       {/* Header Section */}
//       <Box sx={{ p: 3 }}>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'14px', fontWeight: 600, color:'#333333' }}>Cloud Agent</Typography>
//         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 400, color:'#666666'}}>
//           Troubleshoot every issues you face here on the platform
//         </Typography>
//       </Box>

//       {/* Filter Section */}
//       <Box sx={{ px: 3, pb: 3 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start">
//                             <img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} />
//                         </InputAdornment>,
//                     sx: { 
//                         borderRadius: '8px', 
//                         bgcolor: '#F9F9F9', 
//                         width:'265px', 
//                         height:'32px',
//                         '& .MuiOutlinedInput-notchedOutline': {
//                             borderColor: '#E6E6E6'
//                         }
//                     }
//                 }}
//              />
//             <FormControl size="small">
//                 <Select
//                     value={timeRange}
//                     onChange={(e) => setTimeRange(e.target.value)}
//                     sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6E6E6'} }}
//                     IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#666666' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter', fontSize:'12px', fontWeight: 500, color:'#333333' }}>
//                           {timeRangeOptions[selectedValue as string]}
//                         </Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>

//       {/* Content Section */}
//       <Box sx={{ px: 3, pb: 3 }}>
//         <Paper 
//           elevation={0} 
//           sx={{ 
//             borderRadius: '12px', 
//             bgcolor: 'white', 
//             position: 'relative', 
//             overflow: 'hidden' 
//           }}
//         >
//           {/* Background Gradient Image */}
//           {!activeChatId && (
//             <img
//               src="/icons/Gradient.svg" // Your custom image path
//               alt="background gradient"
//               style={{
//                 position: 'absolute',
//                 top: 0,
//                 left: '50%',
//                 transform: 'translateX(-50%)',
//                 width: '100%',
//                 maxWidth: '1200px',
//                 height: 'auto',
//                 objectFit: 'contain',
//                 opacity: 1, // Your desired opacity
//                 zIndex: 0,
//               }}
//             />
//           )}

//           {activeChatId ? (
//             <Box sx={{p:3}}>
//               <ChatDetailView chatId={activeChatId} onBack={handleGoBack} />
//             </Box>
//           ) : (
//             renderLandingPage()
//           )}
//         </Paper>
//       </Box>

//     </Box>
//   );
// };

// export default TroubleshootingPage;




// import React, { useState } from 'react';
// import {
//   Box,
//   Typography,
//   TextField,
//   IconButton,
//   Paper,
//   MenuItem,
//   Select,
//   FormControl,
//   InputAdornment,
//   CircularProgress,
// } from '@mui/material';
// import { Send as SendIcon, Search as SearchIcon } from '@mui/icons-material';
// import ChatDetailView from './ChatDetailView';
// import PreviousChatsList from './PreviousChatsList';

// const mockPreviousChats = [
//   { id: 'chat1', title: "A pod in project 'cust-x' is stuck in pending...", timestamp: '2024-06-23T12:30:00Z', connection: 'Project: CUST-X' },
//   { id: 'chat2', title: 'What is the status of "Search New Data Source"...', timestamp: '2024-06-22T11:00:00Z', connection: 'Project: FINOPS-INTERNAL' },
//   { id: 'chat3', title: 'Lorem ipsum dolor sit amet consectetur adipiscing elit sed...', timestamp: '2024-06-21T15:45:00Z', connection: 'Project: WEB-APP' },
//   { id: 'chat4', title: 'Help me understand xyz and give detailed explanation...', timestamp: '2024-06-20T09:00:00Z', connection: 'Project: DATA-PIPELINE' },
//   { id: 'chat5', title: 'Lorem ipsum dolor sum it...', timestamp: '2024-06-19T18:20:00Z', connection: 'Project: CUST-X' },
// ];

// const mockConnections = [
//     'Project: CUST-X',
//     'Project: FINOPS-INTERNAL',
//     'Project: WEB-APP',
//     'Project: DATA-PIPELINE',
//     'All Connections'
// ];
// const timeRangeOptions: { [key: string]: string } = {
//   'last_30_days': 'Last 30 days',
//   'last_7_days': 'Last 7 days',
//   'last_24_hours': 'Last 24 hours'
// };

// const CustomSelectIcon = (props: any) => {
//     return (
//         <img
//             src="/icons/arrow-down.svg" // <-- IMPORTANT: Replace with the actual path to your icon
//             alt="dropdown icon"
//             style={{ width: '16px', height: '16px', right: 12, position: 'absolute', pointerEvents: 'none' }}
//         />
//     );
// };
// const TroubleshootingPage: React.FC = () => {
//   const [activeChatId, setActiveChatId] = useState<string | null>(null);
//   const [prompt, setPrompt] = useState("A pod in project 'cust-x' is stuck in pending");
//   const [timeRange, setTimeRange] = useState('last_30_days');
//   const [connection, setConnection] = useState('All Connections');
//   const [isSending, setIsSending] = useState(false);

//   const handleStartNewChat = () => {
//     if (prompt.trim()) {
//       setIsSending(true);
//       setTimeout(() => {
//         const newChatId = `chat${mockPreviousChats.length + 1}`;
//         setActiveChatId(newChatId);
//         setIsSending(false);
//       }, 1000);
//     }
//   };

//   const handleSelectChat = (chatId: string) => {
//     const selectedChat = mockPreviousChats.find(c => c.id === chatId);
//     if(selectedChat) {
//         setConnection(selectedChat.connection);
//     }
//     setActiveChatId(chatId);
//   };

//   const handleGoBack = () => {
//     setActiveChatId(null);
//   };

//   const renderLandingPage = () => (
//     <Box sx={{
//         p: 3,
//         display: 'flex',
//         flexDirection: 'column',
//         alignItems: 'center',
//         position: 'relative',
//         zIndex: 1,
//     }}>
//       <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: '#2C3E50' }}>
//         What can I help you with today?
//       </Typography>
//       <Typography color="text.secondary" sx={{ mb: 4 }}>
//         Lorem ipsum dolor sit amet consectetur adipiscing elit sed
//       </Typography>

//       <Box
//         component="form"
//         onSubmit={(e) => { e.preventDefault(); handleStartNewChat(); }}
//         sx={{
//             p: 1.5,
//             display: 'flex',
//             alignItems: 'center',
//             width: '100%',
//             maxWidth: 700,
//             borderRadius: '28px',
//             bgcolor: 'rgba(255, 255, 255, 0.8)',
//             backdropFilter: 'blur(10px)',
//             border: '1px solid rgba(0, 0, 0, 0.1)',
//             boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
//             position: 'relative'
//         }}
//       >
//         <TextField
//           fullWidth
//           variant="standard"
//           placeholder="Enter your prompt here..."
//           value={prompt}
//           onChange={(e) => setPrompt(e.target.value)}
//           sx={{ ml: 2, flex: 1 }}
//           InputProps={{
//             disableUnderline: true,
//             style: { fontSize: '1rem' }
//           }}
//           disabled={isSending}
//         />
//         <IconButton
//           type="submit"
//           sx={{
//             width: 40,
//             height: 40,
//             bgcolor: '#0064FF',
//             color: 'white',
//             '&:hover': { bgcolor: '#0052cc' },
//             '&.Mui-disabled': { bgcolor: 'grey.300' }
//           }}
//           disabled={!prompt.trim() || isSending}
//         >
//           {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon sx={{ transform: 'rotate(-45deg)', mr: -0.5, mt: 0.5 }} />}
//         </IconButton>
//       </Box>
      
//       <PreviousChatsList chats={mockPreviousChats} onSelectChat={handleSelectChat} />
//     </Box>
//   );

//   return (
//     <Box sx={{ width: '100%', bgcolor: '#F9FAFB', minHeight: '100%', position: 'relative', overflow: 'hidden' }}>
//       {/* Background Gradient Image */}
//       {!activeChatId && (
//         <img
//           src="/icons/Gradient.svg" // Replace with your actual image path
//           alt="background gradient"
//           style={{
//             position: 'absolute',
//             //top: 250, // Adjusted top position
//             left: '50%',
//             transform: 'translateX(-50%)',
//             width: '100%',
//             maxWidth: '1200px',
//             height: 'auto',
//             objectFit: 'contain',
//             opacity: 1 ,
//             zIndex: 0,
//           }}
//         />
//       )}
      
//       {/* Header Section */}
//       <Box sx={{ p: 3 }}>
//         <Typography sx={{ fontFamily:'Inter' , fontSize:'14px', fontWeight: 600, fontColor:'#333333' }}>Cloud Agent</Typography>
//         <Typography  sx={{ fontFamily:'Inter' , fontSize:'12px', fontWeight: 400, fontColor:'#666666'}}>
//           Troubleshoot every issues you face here on the platform
//         </Typography>
//       </Box>

//       {/* Filter Section */}
//       <Box sx={{ px: 3, pb: 3 }}>
//         <Paper elevation={0} sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: '10px', bgcolor: '#FFFFFF' }}>
//              <TextField
//                 variant="outlined"
//                 size="small"
//                 placeholder="Search"
//                 InputProps={{
//                     startAdornment: <InputAdornment position="start">
//                             <img src="/icons/search-normal.svg" alt="search" style={{ width: 16, height: 16 }} />
//                         </InputAdornment>,
//                     sx: { borderRadius: '8px', bgcolor: '#F9F9F9', width:'265px', height:'32px', borderColor:'#E6E6E6'}
//                 }}
//              />
//             <FormControl size="small">
//                 <Select
//                     value={timeRange}
//                     onChange={(e) => setTimeRange(e.target.value)}
//                     sx={{ borderRadius: '8px', bgcolor: '#FFFFFF', width:'181px', height:'32px', borderColor:'#E6E6E6' }}
//                     IconComponent={CustomSelectIcon}
//                     renderValue={(selectedValue) => (
//                       <Box sx={{ display: 'flex', gap: 0.5 }}>
//                         <Typography sx={{ fontFamily:'Inter' , fontSize:'12px', fontWeight: 500, fontColor:'#333333' }}>Show for:</Typography>
//                         <Typography sx={{ fontFamily:'Inter' , fontSize:'12px', fontWeight: 500, fontColor:'#333333' }}>
//                           {timeRangeOptions[selectedValue as string]}
//                         </Typography>
//                       </Box>
//                     )}
//                 >
//                     <MenuItem value="last_30_days">{timeRangeOptions['last_30_days']}</MenuItem>
//                     <MenuItem value="last_7_days">{timeRangeOptions['last_7_days']}</MenuItem>
//                     <MenuItem value="last_24_hours">{timeRangeOptions['last_24_hours']}</MenuItem>
//                 </Select>
//             </FormControl>
//         </Paper>
//       </Box>

//       {/* Content Section */}
//       {activeChatId ? (
//         <Box sx={{ px: '24px', pb: '24px' }}>
//             <ChatDetailView chatId={activeChatId} onBack={handleGoBack} />
//         </Box>
//       ) : (
//         renderLandingPage()
//       )}
//     </Box>
//   );
// };

// export default TroubleshootingPage;
